/*
 * This file is part of the Akershus Eiendom Nettsted 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import Arrow from 'components/Icons/Arrow/Arrow';
import Image from '../Image/Image';
import Share from './Share/Share';
import './Receipt.scss';

/**
 * This is the Receipt component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */
function Receipt({
  heading,
  text,
  share,
  buttons,
  image,
  labels,
  onMount,
  onBack,
}) {
  useEffect(onMount, []);

  function renderText() {
    if (!text) return null;
    return <div className="body" dangerouslySetInnerHTML={{ __html: text }} />;
  }

  function renderImage() {
    if (!image) return null;
    return <Image {...image} />;
  }

  return (
    <div className="receipt">
      <div className="receipt-content">
        <h3>{heading}</h3>
        {renderText()}
        <div className="actions">
          {buttons.map(({ color, text, ...props }, index) => (
            <a key={index} className={`button ${color}`} {...props}>
              {text}
            </a>
          ))}
          <Share {...share} />
          <div className="prev">
            <button type="button" onClick={onBack}>
              <span>
                <Arrow left />
                <span>{labels.back}</span>
              </span>
            </button>
          </div>
        </div>
      </div>
      {renderImage()}
    </div>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
Receipt.propTypes = {
  heading: PropTypes.string,
  buttons: PropTypes.arrayOf(
    PropTypes.shape({
      color: PropTypes.string,
      text: PropTypes.string,
    })
  ).isRequired,
  onBack: PropTypes.func.isRequired,
  onMount: PropTypes.func.isRequired,
  share: PropTypes.object.isRequired,
  labels: PropTypes.shape({
    share: PropTypes.string,
    back: PropTypes.string,
  }).isRequired,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
Receipt.defaultProps = {
  buttons: [],
  share: {},
  labels: {
    share: 'Share',
    back: 'Back',
  },
  onMount: () => {},
  onBack: () => {},
};

export default Receipt;
